<template>
  <section class="n-view-register h-screen p-10 flex justify-center items-center">
    <NCard class="w-full max-w-[450px] shadow-md">
      <div class="text-center">
        <img class="mx-auto p-8" src="/images/logo-dark.png" alt="" />
      </div>
      <div class="text-lg p-1">
        <NAsyncContainer :loading="verifyLoading" :blank="true">
          <template v-if="resultOk.success">
            <NForm ref="form" :model="model" :rules="rules" class="!grid-cols-1" @keydown.enter="onPost()">
              <NInputSafePassword v-model:value="model.password" v-bind="passwordAttrs" @change="onConfirmPasswordValidate()" />
              <NInputSafePassword v-model:value="model.confirmPassword" v-bind="confirmPasswordAttrs" />
              <NButton class="n-register-button" size="large" block type="primary" :loading="loading" @click="onPost()">
                <span>{{ $tl('confirm') }}</span>
              </NButton>
            </NForm>
          </template>

          <template v-else>
            <NForm ref="form" :model="model" class="!grid-cols-1">
              <div class="grid grid-cols-12 gap-1.5 pt-4 ">
                <div class="col-start-2 col-end-8 text-center ">
                  <NFormItem>
                    <label class="n-username-bolt">{{ mask }}</label>
                  </NFormItem>
                </div>
                <div class="col-start-9 col-end-11 text-center">
                  <NTimer :seconds="timer_count" @timeOut="onTimeOut" @onResend="onResendCode"/>
                </div>
              </div>

              <div class="pt-4 text-center">
                <NInputVCode @afterFinished="afterVCFinish"/>
              </div>

              <template v-if="resultNg.errorCode">
                <div class="pt-4 text-center">
                  <span class="text-red-600 font-bold">{{ t(resultNg.errorCode, PREFIX.ERROR_CODE) }}</span>
                </div>
              </template>

              <div class="pt-4 text-center">
                <RouterLink to="/forgot">{{ $tl('back') }}</RouterLink>
              </div>

            </NForm>
          </template>
<!--          <template #loading>-->
<!--            <NSpin />-->
<!--          </template>-->
        </NAsyncContainer>
      </div>
      <div class="mt-10 mb-3 text-center text-base text-gray-600 grid items-center gap-3 grid-flow-col place-content-center">
        {{ COPYRIGHT }}
        <NChannelSwitch />
        <NLanguageSwitcher />
        <NHelper />
      </div>
    </NCard>
  </section>
</template>

<script setup>
import {domainEvent, useExecute, useLanguage, useModelPost, useRouter, useValidate} from '@front/hooks';
import {checkResetPasswordCode, resendVerify, resetPassword, resetPasswordRequest} from '@front/api/utility';
import {computed, onMounted, reactive, ref} from 'vue';
import {COPYRIGHT, USERNAME_PROVIDER_TYPE ,VALIDATION_RULE} from '@front/constant';
import { maskUsername } from '@front/utils';
import {IgnoredMessage} from '@/models';

const { tl, t, PREFIX } = useLanguage();
const { router, routerTo } = useRouter();
const verifyLoading = ref(false);
// const code = computed(() => router.currentRoute.value.params.code);
// const launchScheme = computed(() => router.currentRoute.value.params.launchScheme ?? 'pc');
// const lauSch = ref(encodeURIComponent(launchScheme.value));

const username = computed(() => decodeURIComponent(router.currentRoute.value.params.username));
const type = computed(() => decodeURIComponent(router.currentRoute.value.params.type));
const isMail = computed(() => type.value === USERNAME_PROVIDER_TYPE.MAIL_PASSWORD);
const mask = computed(() => isMail.value ? username.value : (maskUsername(username.value, 3)));


const code = ref(undefined);
const intervalId = ref(undefined);
const isDev = import.meta.env.isDev;
const timer_count = ref(isDev ? 5 : 60);

const model = reactive({ password: '', confirmPassword: '', code: code });

const { form, loading, validate, onPost } = useModelPost({
  post: passwordReset,
  // successPath: '/passwordResetNotice/' + lauSch.value,
  successPath: '/passwordResetNotice',
});

const resultOk = reactive({
  success: false,
  data: null,
});
const resultNg = reactive({
  success: false,
  errorCode: null,
  message: null,
});

const { execute } = useExecute({
  execute: async () => {
    try {
      verifyLoading.value = true;
      const r = await checkResetPasswordCode({ code: code.value, username: username.value });
      resultOk.success = r.success;
      resultOk.data = r.data;

    } catch (err) {
      resultNg.success = err.extra?.success;
      resultNg.errorCode = err.extra?.errorCode;
      resultNg.message = err.extra?.message;
    } finally {
      verifyLoading.value = false;
    }
    return true;
  },
});

onMounted(() => {
  // execute();
  startTimer();
});

const validation = computed(() => ({
  password: {
    required: resultOk.success,
    maxLength: VALIDATION_RULE.Customer.password.maxLength,
  },
  confirmPassword: {
    required: resultOk.success,
    same: model.password,
  },
}));
const { rules, clearValidate, validateFields } = useValidate({ formRef: form, validation });

const passwordAttrs = computed(() => ({
  size: 'large',
  type: 'password',
  name: 'password',
  placeholder: tl('newPassword'),
  disabled: loading.value,
}));

const confirmPasswordAttrs = computed(() => ({
  size: 'large',
  type: 'password',
  name: 'confirmPassword',
  placeholder: tl('confirmPassword'),
  disabled: loading.value,
}));

async function onConfirmPasswordValidate() {
  await clearValidate(['confirmPassword']);
  await validateFields(['confirmPassword']).catch(() => Promise.reject(new IgnoredMessage()));
}

async function passwordReset() {
  const params = {
    password: model.password,
    username: username.value,
    code: code.value
    // launchScheme: launchScheme.value,
  };

  try {
    await resetPassword(params);
    // lauSch.value = encodeURIComponent(r.data.launchScheme);
    domainEvent.dispatch('LOGOUT');
  } catch (err) {
    throw err;
  }
}

async function afterVCFinish(vcode) {
  code.value = vcode;
  const result = await execute();
}

async function onResendCode(){
  const r = await resetPasswordRequest({username: username.value}); // 没有type
  if(r.success){
    timer_count.value = 5;
    startTimer();
  }
}

function onTimeOut(){
}

function startTimer(){
  intervalId.value = setInterval(() => {
    if(timer_count.value > 0){
      timer_count.value = timer_count.value - 1;
    } else {
      hadTimeout();
    }
  }, 1000);

}

function hadTimeout(){
  if (intervalId.value) {
    clearInterval(intervalId.value);
    intervalId.value = undefined;
  }
}

</script>

<style lang="less">
.n-view-register {
  background-image: url('/images/shape01.png');
  background-position: center;

  .n-language-switcher {
    gap: 6px;
  }
}
</style>
